// SubscriptionContext.js - Contexto global para compartir el estado de suscripción
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import supabase, { onAuthStateChange, getCurrentUser, checkSession } from '../supabaseClientFixed';
import { validateSubscription } from '../services/culqi';

// Configuración de administradores
const ADMIN_EMAILS = ['alexisgc90@gmail.com'];
const isDevelopment = process.env.NODE_ENV === 'development' || 
                      window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';

// Crear el contexto
const SubscriptionContext = createContext(null);

// Hook para usar el contexto
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription debe usarse dentro de SubscriptionProvider');
  }
  return context;
};

// Provider del contexto
export const SubscriptionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const checkingRef = useRef(false);
  const hasInitialized = useRef(false);
  const currentAccessTokenRef = useRef(null); // Para comparar tokens y evitar re-renders
  
  // En desarrollo, SIEMPRE admin desde el inicio
  const initialSubscription = isDevelopment ? {
    active: true,
    plan: 'admin',
    limits: null,
    usage: null,
    isAdmin: true,
    isDevelopment: true
  } : {
    active: false,
    plan: 'free',
    limits: null,
    usage: null
  };
  
  const [subscription, setSubscription] = useState(initialSubscription);
  const [loading, setLoading] = useState(true);
  
  console.log('🎯 SubscriptionContext inicial:', {
    isDevelopment,
    initialSubscription
  });

  // Límites por plan actualizados
  const planLimits = {
    anonymous: {
      questionsPerSession: 5,
      hasSimulationMode: false,
      requiresLogin: true,
      maxDevices: 1,
      hasExplanations: false,
      hasStatistics: false
    },
    free: {
      questionsPerSession: 15,
      hasSimulationMode: false,
      requiresUpgrade: true,
      maxDevices: 1,
      hasExplanations: false,
      hasStatistics: true
    },
    pro: {
      questionsPerSession: null,
      hasSimulationMode: true,
      requiresUpgrade: false,
      maxDevices: 3,
      hasExplanations: true,
      hasStatistics: true
    }
  };

  const checkSubscription = async () => {
    // Evitar múltiples verificaciones simultáneas
    if (checkingRef.current) {
      console.log('⏳ Ya hay una verificación en progreso');
      return;
    }
    
    // Debounce: evitar llamadas muy seguidas
    const now = Date.now();
    if (window._lastSubscriptionCheck && (now - window._lastSubscriptionCheck) < 2000) {
      console.log('⏸️ Debounce: evitando verificación muy seguida');
      return;
    }
    window._lastSubscriptionCheck = now;
    
    console.log('🔄 checkSubscription llamado UNA VEZ');
    
    // EN DESARROLLO: SIEMPRE ADMIN
    if (isDevelopment) {
      console.log('👑 DESARROLLO - Modo Admin');
      setUser(null);
      setSubscription({
        active: true,
        plan: 'admin',
        limits: null,
        usage: null,
        isAdmin: true,
        isDevelopment: true
      });
      setLoading(false);
      return;
    }
    
    checkingRef.current = true;
    setLoading(true);
    
    try {
      const currentUser = await getCurrentUser();
      
      console.log('📌 Usuario actual:', currentUser?.email || 'Sin usuario');
      
      if (!currentUser) {
        setUser(null);
        setSubscription({
          active: false,
          plan: 'free',
          limits: planLimits.free,
          usage: null,
          isAdmin: false
        });
        setLoading(false);
        return;
      }
      
      setUser(currentUser);
      
      // Verificar admin por email
      if (ADMIN_EMAILS.includes(currentUser.email)) {
        console.log('👑 Admin detectado:', currentUser.email);
        setSubscription({
          active: true,
          plan: 'admin',
          limits: null,
          usage: null,
          isAdmin: true,
          isAdminEmail: true
        });
        setLoading(false);
        return;
      }

      // Validar suscripción normal
      const subData = await validateSubscription(currentUser.id);
      const limits = planLimits[subData.plan] || planLimits.free;
      const usage = await getUserUsage(currentUser.id);
      
      setSubscription({
        ...subData,
        limits,
        usage,
        isAdmin: false
      });
    } catch (error) {
      console.error('Error verificando suscripción:', error);
      setSubscription({
        active: false,
        plan: 'free',
        limits: planLimits.free,
        usage: null,
        isAdmin: false
      });
    } finally {
      setLoading(false);
      checkingRef.current = false;
    }
  };

  const getUserUsage = async (userId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: dailyUsage, error: dailyError } = await supabase
        .from('user_daily_usage')
        .select('*')
        .eq('user_id', userId)
        .eq('date', today)
        .single();

      if (dailyError && dailyError.code !== 'PGRST116') {
        console.error('Error obteniendo uso diario:', dailyError);
      }

      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const { data: monthlyUsage, error: monthlyError } = await supabase
        .from('user_daily_usage')
        .select('simulations_taken')
        .eq('user_id', userId)
        .gte('date', startOfMonth.toISOString().split('T')[0]);

      if (monthlyError) {
        console.error('Error obteniendo uso mensual:', monthlyError);
      }

      const totalSimulations = monthlyUsage?.reduce((sum, day) => sum + (day.simulations_taken || 0), 0) || 0;

      return {
        questionsToday: dailyUsage?.questions_answered || 0,
        simulationsThisMonth: totalSimulations,
        studyTimeToday: dailyUsage?.study_time_minutes || 0
      };
    } catch (error) {
      console.error('Error obteniendo uso:', error);
      return {
        questionsToday: 0,
        simulationsThisMonth: 0,
        studyTimeToday: 0
      };
    }
  };

  useEffect(() => {
    // Solo ejecutar una vez
    if (hasInitialized.current) {
      console.log('⏭️ Provider ya inicializado');
      return;
    }
    hasInitialized.current = true;
    
    console.log('🚀 Inicializando SubscriptionProvider');
    
    // En desarrollo, mantener estado admin
    if (isDevelopment) {
      console.log('🎮 Desarrollo: Estado admin');
      setLoading(false);
      return;
    }
    
    // Verificar sesión inicial con delay para evitar parpadeo
    const timeoutId = setTimeout(() => {
      if (!checkingRef.current) {
        checkSubscription();
      }
    }, 100);
    
    // Obtener sesión inicial primero para evitar parpadeo
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        currentAccessTokenRef.current = session.access_token;
        // Solo verificar suscripción si hay sesión inicial
        if (!checkingRef.current) {
          checkSubscription();
        }
      } else {
        setLoading(false);
      }
    });
    
    // Escuchar cambios de autenticación - SOLO cambios reales
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔔 Evento auth recibido:', event);
        
        // Comparar tokens para detectar cambios reales
        const newAccessToken = session?.access_token;
        const hasTokenChanged = newAccessToken !== currentAccessTokenRef.current;
        
        // Solo procesar si realmente cambió el token
        if (!hasTokenChanged && event !== 'SIGNED_OUT') {
          console.log('🚫 Token no cambió, ignorando evento');
          return;
        }
        
        // Actualizar token de referencia
        currentAccessTokenRef.current = newAccessToken;
        
        if (event === 'SIGNED_IN' && hasTokenChanged) {
          console.log('✅ Sesión iniciada (token nuevo)');
          setUser(session?.user || null);
          // Usar setTimeout para evitar operaciones async en callback
          setTimeout(() => {
            if (!checkingRef.current) {
              checkSubscription();
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          console.log('🚪 Sesión cerrada');
          setUser(null);
          setSubscription({
            active: false,
            plan: 'free',
            limits: planLimits.free,
            usage: null
          });
          setLoading(false);
        } else if (event === 'USER_UPDATED' && hasTokenChanged) {
          console.log('🔄 Usuario actualizado');
          setUser(session?.user || null);
        }
      }
    );
    
    return () => {
      clearTimeout(timeoutId);
      authSubscription?.unsubscribe();
      hasInitialized.current = false;
    };
  }, []); // Solo ejecutar una vez

  // Funciones helper
  const canAnswerQuestion = (currentQuestionNumber) => {
    if (isDevelopment || subscription.isAdmin || subscription.plan === 'pro') {
      return { allowed: true };
    }
    
    if (!user && subscription.limits?.requiresLogin) {
      if (currentQuestionNumber > subscription.limits.questionsPerSession) {
        return { 
          allowed: false, 
          reason: 'login_required',
          message: 'Debes iniciar sesión para continuar'
        };
      }
    }
    
    if (subscription.limits?.questionsPerSession && 
        currentQuestionNumber > subscription.limits.questionsPerSession) {
      return { 
        allowed: false, 
        reason: 'limit_reached',
        message: `Has alcanzado el límite de ${subscription.limits.questionsPerSession} preguntas`
      };
    }
    
    return { allowed: true };
  };

  const canGenerateExam = () => {
    if (isDevelopment || subscription.isAdmin || subscription.plan === 'pro') {
      return { allowed: true };
    }
    
    if (!user) {
      return { 
        allowed: false, 
        reason: 'login_required',
        message: 'Debes iniciar sesión para generar exámenes'
      };
    }
    
    return { allowed: true };
  };

  const canTakeSimulation = () => {
    if (isDevelopment || subscription.isAdmin || subscription.plan === 'pro') {
      return { allowed: true };
    }
    
    if (!subscription.limits?.hasSimulationMode) {
      return { 
        allowed: false, 
        reason: 'upgrade_required',
        message: 'El modo simulacro requiere una suscripción Pro'
      };
    }
    
    return { allowed: true };
  };

  const trackQuestionAnswered = async () => {
    if (!user || isDevelopment) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const { error } = await supabase.rpc('increment_questions_answered', {
        p_user_id: user.id,
        p_date: today
      });
      
      if (error) {
        console.error('Error registrando pregunta:', error);
      }
    } catch (error) {
      console.error('Error en trackQuestionAnswered:', error);
    }
  };

  const refreshSubscription = async () => {
    await checkSubscription();
  };

  const signOutCompletely = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setSubscription({
        active: false,
        plan: 'free',
        limits: planLimits.free,
        usage: null
      });
      
      // Limpiar localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('mediexampro')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      return true;
    } catch (error) {
      console.error('Error cerrando sesión:', error);
      throw error;
    }
  };

  const value = {
    user: user,
    subscription,
    loading,
    canAnswerQuestion,
    canGenerateExam,
    canTakeSimulation,
    questionsRemaining: subscription.limits?.questionsPerSession 
      ? Math.max(0, subscription.limits.questionsPerSession - (subscription.usage?.questionsToday || 0))
      : null,
    simulationsRemaining: null,
    trackQuestionAnswered,
    showUpgradePrompt: !user || subscription.plan === 'free',
    refreshSubscription,
    signOutCompletely
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;